import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  getElements,
  createElementChildren as createElementChildrenService,
  updateBasicInformationElementChildren as updateBasicInformationElementChildrenService,
  updateElementsElementStructureId as updateElementsElementStructureIdService,
  deleteElementChildren as deleteElementChildrenService,
  updateTypeElementsElementChildren as updateTypeElementsElementChildrenService
} from "../../services/curriculum/elements.service";
import { changedCurriculum, updateElementsList, updateElementsListMethod } from "../../store/actions/curriculum.actions";
import { useParams } from "react-router-dom";
import { validate } from "../../utils";
import isEmpty from "lodash/isEmpty";
import { handlerErrorV2 } from "../../utils/errors";
import { useSnackbar } from "notistack";

const useCurriculumElements = () => {
  const { curriculumId } = useParams()
  const { enqueueSnackbar } = useSnackbar()

  const dispatch = useDispatch();
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [errors, setErrors] = useState({});

  const fetchLcElement = useCallback(async () => {
    setIsLoadingList(true);
    try {
      const response = await getElements(curriculumId);
      dispatch(updateElementsList(response?.data || {}));
    } catch (error) {
      setErrors({ message: error.message || 'An error ocurred'});
    } finally {
      setIsLoadingList(false);
    }
  }, []);

  useEffect(() => {
    fetchLcElement();
  }, []);

  const validateForm = (form) => {
    const fieldRules = {
      name: 'required',
      shortName: 'required|withoutSpaces'
    }

    const errors = validate(fieldRules, form)
    if (!isEmpty(errors)) {
      return { error: true, ...errors }
    }

    return { error: false }
  }

  const createElementChildren = async (form = {}) => {
    const result = validateForm(form);
    if (result.error) {
      return result
    };

    try {
      const response = await createElementChildrenService(curriculumId, form);
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred'}
      } else {
        enqueueSnackbar('Element created successfully', { variant: 'success' })
        dispatch(updateElementsListMethod('POST', response.data));
        dispatch(changedCurriculum());
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  const updateBasicInformationElementChildren = async (childrenId, form = {}) => {
    const result = validateForm(form);
    if (result.error) {
      return result
    };

    try {
      const response = await updateBasicInformationElementChildrenService(curriculumId, childrenId, form);
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred'}
      } else {
        enqueueSnackbar('Element updated successfully', { variant: 'success' });
        dispatch(changedCurriculum());

        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  const updateElementsElementStructureId = async (childrenId, type, elementId, parent, form = {}) => {
    try {
      const response = await updateElementsElementStructureIdService(curriculumId, type, childrenId, { elementId, parent, form });
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred'}
      } else {
        enqueueSnackbar('Element updated successfully', { variant: 'success' })
        dispatch(changedCurriculum());
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }
  const updateTypeElementsElementChildren = async (childrenId, type, elementId, parent, form = {}) => {
    try {
      const response = await updateTypeElementsElementChildrenService(curriculumId, type, childrenId, { elementId, parent, form });
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred'}
      } else {
        enqueueSnackbar('Element updated successfully', { variant: 'success' })
        dispatch(changedCurriculum());
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  const deleteElementChildren = async (childrenId) => {
    try {
      const response = await deleteElementChildrenService(curriculumId, childrenId);
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred'}
      } else {
        enqueueSnackbar('Element deleted successfully', { variant: 'success' })
        dispatch(updateElementsListMethod('DELETE', childrenId));
        dispatch(changedCurriculum());
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  return {
    errors,
    isLoadingList,
    createElementChildren,
    updateBasicInformationElementChildren,
    updateElementsElementStructureId,
    updateTypeElementsElementChildren,
    deleteElementChildren,
    validateForm
  }
};

export default useCurriculumElements;